/**
 * SkillDetailModal — shows what data/TOPIC-TAXONOMY.yaml declares about one
 * skill on the /skills tab: its category, what it covers, the question that
 * makes the classifier reach for it, and the directive the coach is given when
 * it does. Two distinct interactions on a SkillsPage row:
 *
 *   - Row body click  → TraceDrillDownModal (preloadedSkill filter)
 *   - Details button  → THIS modal (what the skill IS)
 *
 * The detail payload (`SkillDetail`) is fetched lazily — `useSkillDetail`
 * only fires when topicId becomes non-null (modal opens).
 *
 * Layout:
 *   - Wide modal (80rem) so dense directives have room to breathe.
 *   - Header: title + category/covers chips.
 *   - When + How rendered as markdown (remark-breaks so single newlines
 *     become <br>), open by default — they are the whole body now.
 *
 * The synthesized markdown body and the Confluence source list this modal used
 * to lead with went with the `kb_skills` table (migration 012, 2026-09-06): the
 * body was empty on 36 of that table's 37 rows, and nothing computes a source
 * list any more, so an empty "Sources (0)" box would have claimed the skill has
 * none rather than that we stopped tracking them.
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSkillDetail } from '@/hooks/use-operations';
import { cn } from '@/lib/utils';

interface SkillDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** Click-through key. Null while modal is closed; setting it opens the
   *  modal AND triggers the lazy useSkillDetail fetch. */
  topicId: string | null;
  /** Human label rendered in the title before the detail loads — avoids
   *  showing "loading" in the heading. Falls through to detail.topicName
   *  once the fetch resolves. */
  fallbackDisplayName?: string;
  testIdPrefix?: string;
}

/** Shared prose styling — same as TraceDetailPanel for visual consistency. */
const PROSE_CLASSES = cn(
  'prose prose-sm max-w-none dark:prose-invert',
  'prose-p:my-1 prose-headings:my-2 prose-pre:my-1 prose-li:my-0',
  'prose-h1:text-base prose-h2:text-sm prose-h3:text-sm',
  'prose-a:text-primary prose-a:underline-offset-2',
  'prose-code:font-mono prose-code:text-[0.85em]',
  'prose-code:before:content-none prose-code:after:content-none',
  'prose-pre:bg-muted prose-pre:text-foreground prose-pre:overflow-x-auto',
);

/** Markdown renderer — links open in new tab; remark-breaks so directive
 *  text with single newlines (the dominant authoring style here) renders
 *  with <br> instead of being collapsed into one paragraph. */
function Markdown({ children }: { children: string }): React.ReactElement {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        a: ({ node: _node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

/** Collapsible <details>-style block — used for When / How so a very long
 *  directive can be folded away without leaving the modal. */
function CollapsibleSection({
  label,
  defaultOpen,
  children,
  testId,
}: {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  testId: string;
}): React.ReactElement {
  return (
    <details
      open={defaultOpen}
      data-testid={testId}
      className="rounded border border-border bg-muted/30"
    >
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </summary>
      <div
        className={cn(
          'border-t border-border px-3 py-2 text-sm leading-snug break-words bg-background',
          PROSE_CLASSES,
        )}
      >
        {children}
      </div>
    </details>
  );
}

export function SkillDetailModal({
  open,
  onClose,
  topicId,
  fallbackDisplayName,
  testIdPrefix = 'skill-detail',
}: SkillDetailModalProps): React.ReactElement {
  const query = useSkillDetail(open ? topicId : null);
  const detail = query.data;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  const headingText = detail?.topicName ?? fallbackDisplayName ?? topicId ?? '';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={testIdPrefix}
        className="max-w-[min(96vw,80rem)] w-[min(96vw,80rem)] max-h-[92vh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="space-y-1">
          <DialogTitle data-testid={`${testIdPrefix}-title`} className="text-lg">
            {headingText}
          </DialogTitle>

          {/* Compact metadata strip: category + covers as inline chips.
              When/How move out of the header into the body where they get
              proper markdown rendering. */}
          {detail && (detail.category || detail.covers) ? (
            <DialogDescription
              data-testid={`${testIdPrefix}-subtitle`}
              className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground"
            >
              {detail.category ? (
                <span>
                  <span className="font-semibold">Category:</span>{' '}
                  <code className="text-foreground">{detail.category}</code>
                </span>
              ) : null}
              {detail.covers ? (
                <span>
                  <span className="font-semibold">Covers:</span>{' '}
                  <span className="text-foreground">{detail.covers}</span>
                </span>
              ) : null}
            </DialogDescription>
          ) : (
            <DialogDescription
              data-testid={`${testIdPrefix}-subtitle`}
              className="text-xs text-muted-foreground"
            >
              What this skill is and when Mr. Edge reaches for it.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Body — single scroll container. */}
        <div className="min-h-0 flex-1 overflow-y-auto space-y-3 pr-1 pb-1">
          {query.isLoading ? (
            <div
              className="text-sm text-muted-foreground"
              data-testid={`${testIdPrefix}-loading`}
            >
              Loading…
            </div>
          ) : query.isError ? (
            <div
              className="text-sm text-red-600"
              data-testid={`${testIdPrefix}-error`}
            >
              Failed to load skill —{' '}
              <button
                type="button"
                onClick={() => void query.refetch()}
                className="underline"
              >
                retry
              </button>
            </div>
          ) : detail ? (
            <>
              {/* When / How — open by default: with the synthesized body
                  gone these are the whole of what the modal has to show. */}
              {detail.principleQuestion ? (
                <CollapsibleSection
                  label="When to use"
                  defaultOpen
                  testId={`${testIdPrefix}-when`}
                >
                  <Markdown>{detail.principleQuestion}</Markdown>
                </CollapsibleSection>
              ) : null}

              {detail.directive ? (
                <CollapsibleSection
                  label="How to use"
                  defaultOpen
                  testId={`${testIdPrefix}-how`}
                >
                  <Markdown>{detail.directive}</Markdown>
                </CollapsibleSection>
              ) : null}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
